import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileArrowUp, MagnifyingGlass, X } from "@phosphor-icons/react";

// Tipuri comune (disponibile tuturor); peste acestea se adaugă nomenclatorul specific departamentului
const BASE_CATS = ["oferta", "contract", "proforma", "factura", "comanda", "aviz", "cmr", "desen",
  "fisa_proiect", "pv", "certificat", "dovada", "nota_santier", "altul"];
const CAT_LABEL = {
  contract: "Contract", oferta: "Ofertă", factura: "Factură", proforma: "Proformă",
  comanda: "Comandă", aviz: "Aviz", cmr: "CMR", desen: "Desen tehnic", nir: "NIR",
  fisa_proiect: "Fișă proiect", pv: "Proces-verbal", certificat: "Certificat",
  dovada: "Dovadă încasare", nota_santier: "Notă șantier", altul: "Altul",
};
const DONE_STAGES = ["castigat", "pierdut"];

export default function DepartmentUpload({ dept, onUploaded, selectedProject, onProjectChange }) {
  const { user } = useAuth();
  const leadsAccess = canAccess(user, "/leads");
  const [target, setTarget] = useState("project"); // 'project' | 'lead'
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectIdRaw] = useState("");
  const setProjectId = (v) => { setProjectIdRaw(v); onProjectChange && onProjectChange(v); };
  useEffect(() => { if (selectedProject !== undefined) { setProjectIdRaw(selectedProject || ""); } }, [selectedProject]);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [leadQuery, setLeadQuery] = useState("");
  const [leadOpen, setLeadOpen] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [common, setCommon] = useState(BASE_CATS);
  const [category, setCategory] = useState(BASE_CATS[0]);
  const loadTypes = () => {
    if (!dept) return;
    api.get(`/doc-types?department=${dept}`).then((r) => setDocTypes(r.data || [])).catch(() => setDocTypes([]));
    api.get(`/doc-common?department=${dept}`).then((r) => setCommon(r.data?.enabled || BASE_CATS)).catch(() => setCommon(BASE_CATS));
  };
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canMulti = ["proiectare", "proiectare_structuri"].includes(dept) || user?.role === "admin";

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    loadTypes();
    if (leadsAccess) {
      api.get("/leads").then((r) => setLeads((r.data || []).filter((l) => !DONE_STAGES.includes(l.stage)))).catch(() => {});
    }
    // eslint-disable-next-line
  }, [leadsAccess, dept]);

  const onCatChange = async (v) => {
    if (v !== "__add") { setCategory(v); return; }
    const label = (window.prompt("Denumește noul tip de document (se salvează în nomenclatorul departamentului):") || "").trim();
    if (!label) return;
    try {
      await api.post("/doc-types", { department: dept, label });
      loadTypes();
      setCategory(label);
      toast.success(`Tip de document adăugat: ${label}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const leadName = (l) => l.company || l.name || l.contact_name || "Lead";
  const selectedLead = leads.find((l) => l.id === leadId);
  const filteredLeads = leadQuery.trim()
    ? leads.filter((l) => leadName(l).toLowerCase().includes(leadQuery.trim().toLowerCase()))
    : leads;

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    if (target === "project" && !projectId) { toast.error("Alege întâi proiectul"); if (fileRef.current) fileRef.current.value = ""; return; }
    if (target === "lead" && !leadId) { toast.error("Alege întâi leadul"); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    try {
      if (target === "lead") {
        for (const f of list) {
          const fd = new FormData();
          fd.append("file", f); fd.append("category", category); fd.append("name", f.name); fd.append("department", dept);
          await api.post(`/leads/${leadId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success(`${list.length} document(e) adăugate pe leadul „${leadName(selectedLead || {})}"`);
        onUploaded && onUploaded(null);
      } else if (canMulti && list.length > 1) {
        const fd = new FormData();
        list.forEach((f) => fd.append("files", f));
        fd.append("category", category); fd.append("department", dept);
        const { data } = await api.post(`/projects/${projectId}/folder/multi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${data.saved} documente adăugate în dosarul proiectului`);
        onUploaded && onUploaded(projectId);
      } else {
        const fd = new FormData();
        fd.append("file", list[0]); fd.append("category", category); fd.append("name", list[0].name); fd.append("department", dept);
        await api.post(`/projects/${projectId}/folder`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Document adăugat în dosarul digital al proiectului");
        onUploaded && onUploaded(projectId);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-6 rounded-xl border border-border bg-white/[0.02] p-4" data-testid="dept-upload">
      <h2 className="text-sm font-semibold">Adaugă documente în dosarul proiectului</h2>

      {leadsAccess && (
        <div className="mt-3 inline-flex rounded-lg border border-border p-0.5 text-xs" data-testid="dept-upload-target">
          <button onClick={() => setTarget("project")} className={`rounded-md px-3 py-1 font-medium transition-colors ${target === "project" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="dept-upload-target-project">Proiect</button>
          <button onClick={() => setTarget("lead")} className={`rounded-md px-3 py-1 font-medium transition-colors ${target === "lead" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="dept-upload-target-lead">Lead</button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {target === "project" ? (
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="dept-upload-project"><SelectValue placeholder="Alege proiectul…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="relative w-72" data-testid="dept-upload-lead-wrap">
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 h-10">
              <MagnifyingGlass size={15} className="shrink-0 text-muted-foreground" />
              <input
                value={leadId ? leadName(selectedLead || {}) : leadQuery}
                onChange={(e) => { setLeadId(""); setLeadQuery(e.target.value); setLeadOpen(true); }}
                onFocus={() => setLeadOpen(true)}
                placeholder="Caută leadul (ex: PRIMO)…"
                className="h-full w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                data-testid="dept-upload-lead-search"
              />
              {leadId && <button onClick={() => { setLeadId(""); setLeadQuery(""); }} className="text-muted-foreground hover:text-foreground" data-testid="dept-upload-lead-clear"><X size={14} /></button>}
            </div>
            {leadOpen && !leadId && (
              <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg" data-testid="dept-upload-lead-list">
                {filteredLeads.slice(0, 80).map((l) => (
                  <button key={l.id} onClick={() => { setLeadId(l.id); setLeadOpen(false); }} className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-white/5" data-testid={`dept-upload-lead-${l.id}`}>
                    {leadName(l)} <span className="text-[11px] text-muted-foreground">· {l.stage}</span>
                  </button>
                ))}
                {filteredLeads.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">Niciun lead activ găsit.</p>}
              </div>
            )}
          </div>
        )}
        <Select value={category} onValueChange={onCatChange}>
          <SelectTrigger className="w-52" data-testid="dept-upload-category"><SelectValue placeholder="Tip document" /></SelectTrigger>
          <SelectContent>
            {common.map((c) => <SelectItem key={c} value={c}>{CAT_LABEL[c] || c}</SelectItem>)}
            {docTypes.map((t) => <SelectItem key={t.id} value={t.label} data-testid={`dept-upload-cat-${t.id}`}>{t.label}</SelectItem>)}
            <SelectItem value="__add">+ Altul (adaugă tip nou)…</SelectItem>
          </SelectContent>
        </Select>
        <input ref={fileRef} type="file" multiple={canMulti} className="hidden" onChange={onPick} data-testid="dept-upload-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dept-upload-btn">
          <FileArrowUp size={16} className="mr-1.5" /> {uploading ? "Se încarcă…" : (canMulti ? "Adaugă documente (mai multe)" : "Adaugă documente")}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {target === "lead"
          ? "Se afișează doar leadurile active (care nu sunt câștigate sau pierdute). Documentul se salvează pe leadul ales."
          : "Fișierul se salvează în subfolderul departamentului, în dosarul digital al proiectului ales."}
      </p>
    </div>
  );
}
